<template lang="pug">
    layout
        template(slot="title")

            // Title
            title-component(:title="$t('data.title')" type="h1")

        div
            div.dataPackages

                // Subscriptions
                data-package-component(
                    v-if="packages && packages.length"
                    v-for="item in packages"
                    :item="item"
                    :key="item.id"
                    :savedCard="savedCard"
                    :subscribeCallback="subscribeCallback"
                )

            div(v-show="savedCard && savedCard.id")

                // Saved card
                saved-card(:card="savedCard" :deleteCallback="getSavedPayments")

            div(v-show="!savedCard")
                div Please enter your payment details:

                // Stripe card component
                div.stripeCard
                    div(ref="card")

                div.text-right.mt-4

                    // Submit button
                    v-btn.mr-0.submit(
                        type="submit"
                        color="primary"
                        large
                        @click='pay'
                        :disabled="isLoading"
                        :loading="isLoading"
                    ) {{ $t('fields.addCard') }}
</template>

<script>
    import Layout from '../Layout';
    import { Title, DataPackage } from '~components';
    import { payments } from '~api';
    import { Handler } from '~services';
    import { mapActions, mapGetters } from 'vuex';
    import SavedCard from './SavedCard';

    // eslint-disable-next-line no-undef
    let stripe = Stripe(process.env.VUE_APP_STRIPE_KEY),
        elements = stripe.elements(),
        card = undefined;

    let style = {
        base: {
            padding: 20,
            iconColor: '#9E9E9E',
            color: '#000',
            fontWeight: 500,
            fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
            fontSize: '16px',
            fontSmoothing: 'antialiased',
            ':-webkit-autofill': {
                color: '#000',
            },
            '::placeholder': {
                color: '#9E9E9E',
            },
        },
    };

    export default {
        components: {
            Layout,
            SavedCard,
            TitleComponent: Title,
            DataPackageComponent: DataPackage,
        },
        data() {
            return {
                isLoading: false,
                packages: [],
                complete: false,
                savedCard: {},
            };
        },
        computed: {
            ...mapGetters('auth', ['user']),
        },
        async created() {
            await this.getSavedPayments();
            await this.getSubscriptionsList();
            this.initStripeComponent();
        },
        beforeDestroy() {
            if (card) card.destroy();
        },
        methods: {
            ...mapActions('prompt', ['show']),
            async pay() {
                this.isLoading = true;

                let setupIntent;
                const self = this;
                const respSetupIntent = await payments.getSetupIntent();
                if (respSetupIntent.ok) setupIntent = respSetupIntent.data;

                const billingDetails = {
                    name: `${self.user.first_name} ${self.user.last_name}`,
                    email: self.user.email,
                };

                stripe
                    .confirmCardSetup(setupIntent, {
                        payment_method: {
                            card: card,
                            billing_details: billingDetails,
                        },
                    })
                    .then(function (result) {
                        if (result.setupIntent && result.setupIntent.id) {
                            const data = new FormData();
                            data.append(
                                'pm',
                                result.setupIntent.payment_method
                            );
                            data.append('seti', result.setupIntent.id);

                            return self.setDefaultPayment(data);
                        }

                        return (self.isLoading = false);
                    });
            },

            async setDefaultPayment(params) {
                const respSetPayment = await payments.setDefaultPayment(params);

                if (respSetPayment.ok) {
                    Handler.handleResponse(
                        respSetPayment,
                        respSetPayment.data,
                        respSetPayment.data
                    );

                    return this.getSavedPayments();
                }

                return (this.isLoading = false);
            },

            async subscribeCallback(params) {
                // Show confirm modal window
                const promptParams = {
                    title: 'Subscription',
                    description: 'Please confirm change',
                    confirmButton: 'confirm',
                    declineButton: 'cancel',
                };
                const confirmed = await this.show(promptParams);

                if (!confirmed) return;

                const resp = await payments.subscribeOnDataset(params);

                Handler.handleResponse(resp, resp.data, resp.data);

                if (resp.ok) this.getSubscriptionsList();
            },

            /**
             * Get saved cards
             */
            async getSavedPayments() {
                const respSavedPayments = await payments.getPaymentMethod();

                if (respSavedPayments.ok)
                    this.savedCard = respSavedPayments.data[0];

                this.isLoading = false;
            },

            /**
             * Get Subscriptions list
             * @returns {Promise<void>}
             */
            async getSubscriptionsList() {
                const resp = await payments.getSubscriptionsList();

                if (resp.ok) this.packages = resp.data;
            },

            /**
             * Init Stripe component
             */
            initStripeComponent() {
                card = elements.create('card', { style });
                card.mount(this.$refs.card);
            },
        },
    };
</script>

<style lang="stylus" scoped>
    .stripeCard
        border: 1px solid rgba(0, 0, 0, 0.38)
        border-radius: 5px
        padding: 12px 7px
</style>
